/**
 * shadcn/ui-style wrapper around Radix ``@radix-ui/react-tooltip`` — the single
 * shared tooltip primitive mandated by REQ-UI-133.
 *
 * Mirrors the sibling :file:`./context-menu.tsx` / :file:`./dropdown-menu.tsx`
 * wrappers exactly — the ``cn`` merge helper, ``forwardRef`` + a named function
 * component, Meridian design-system tokens from ``shared/design-system.md``
 * (``bg-bg-overlay`` surface, ``border-border``, ``text-text-primary`` body).
 * ``@radix-ui/react-tooltip`` was already a declared dependency with **zero
 * import sites**; this is its first use, so no CLAUDE.md §4.3 vetting applies
 * and nothing was added to the dependency set.
 *
 * # Why one shared primitive, and why it is not hover-only
 *
 * Two consumers arrive together: REQ-STATUS-027's two health-row descriptions
 * ("Serving" / "Overall") and REQ-STATUS-050's ~60 metric-catalog descriptions.
 * Authoring the affordance once keeps them from each inventing their own
 * accessibility story (REQ-UI-133 rationale).
 *
 * **Radix Tooltip is not keyboard-reachable on a non-focusable element** — which
 * describes a ``<dt>``/``<dd>`` in a definition list exactly. A hover-only
 * tooltip passes a mouse-driven review and fails every keyboard user, so it is a
 * defect rather than a missing nicety (REQ-UI-059; CLAUDE.md §5.11, where
 * keyboard navigation is part of the mandatory visual pass). The composed
 * :func:`DescriptionTooltip` therefore hangs the description off a real
 * ``<button>``: focusable for free, in the tab order for free, announced as an
 * interactive control for free, and — per Radix — carrying ``aria-describedby``
 * to the content while open.
 *
 * # Text, never markup
 *
 * Catalog descriptions are **operator-editable deployment configuration**
 * (REQ-STATUS-051's ``MERIDIAN_BFF_METRIC_LABELS_FILE`` overlay). They are
 * rendered as React text children — never ``dangerouslySetInnerHTML``, never a
 * ``title``-attribute HTML path — because a config file reaching a template
 * context would be a stored-injection surface on an authenticated page
 * (REQ-STATUS-056 Notes; CLAUDE.md §8.1 conservative defaults).
 *
 * Cross-references:
 *   - REQ-UI-133 (the shared primitive) / REQ-STATUS-056 (metric-row tooltips).
 *   - REQ-STATUS-027 (the Serving / Overall descriptions, text fixed verbatim).
 *   - REQ-UI-028 (shadcn/ui component layer); REQ-UI-059 / REQ-UI-094 (a11y).
 *   - shared/design-system.md (overlay tokens).
 */
import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cn } from "@/lib/utils";

/**
 * Milliseconds a pointer must rest on a trigger before the tooltip opens.
 * Shorter than Radix's 700 ms default: an operator sweeping a dense Selection
 * Detail should not have to learn to wait. Keyboard focus opens immediately and
 * ignores this delay.
 */
const TOOLTIP_DELAY_MS = 250;

/**
 * Radix's delay-grouping provider. Re-exported for a future surface that wants
 * one shared "skip the delay for the next tooltip" group across many triggers;
 * :func:`Tooltip` already supplies its own provider, so ordinary callers never
 * need this.
 */
export const TooltipProvider = TooltipPrimitive.Provider;

/**
 * A single tooltip: trigger + portalled content.
 *
 * Wraps its own ``Provider`` so a caller never has to remember a global one —
 * Radix throws without a provider in the tree, and a crash-on-forget seam is a
 * poor primitive. Nesting providers is supported by Radix, so this composes
 * safely inside a caller-supplied :func:`TooltipProvider` group.
 */
export function Tooltip({
  delayDuration = TOOLTIP_DELAY_MS,
  ...props
}: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Root>): React.ReactElement {
  return (
    <TooltipPrimitive.Provider delayDuration={delayDuration}>
      <TooltipPrimitive.Root delayDuration={delayDuration} {...props} />
    </TooltipPrimitive.Provider>
  );
}

/**
 * The element the tooltip hangs off. Render a focusable control as
 * ``<TooltipTrigger asChild>`` so that element itself is the trigger, keeping
 * its own ``aria-label`` / classes; Radix wires focus, pointer and
 * ``aria-describedby`` onto the child.
 *
 * ⚠ ``asChild`` onto a **non-focusable** element (a ``<span>``, a ``<dt>``)
 * produces a hover-only tooltip — the defect REQ-UI-133 exists to prevent. Use
 * :func:`DescriptionTooltip`, or pass a genuinely focusable child.
 */
export const TooltipTrigger = TooltipPrimitive.Trigger;

/**
 * The portalled tooltip surface. Styled as a Meridian overlay
 * (``bg-bg-overlay`` + ``border-border`` + ``shadow-lg``) and portalled to
 * ``<body>`` so it escapes the Entity Details panel's ``overflow-y-auto`` clip
 * and z-stack. Width-capped so a 400-character catalog description wraps into a
 * readable column rather than a single long line.
 *
 * ⚠ Portalling puts the content **outside** ``.system-status-scope``, so the
 * panel-scoped status-token overrides (globals.css) do not reach in here. The
 * three tokens above are global and unaffected — but a status colour rendered
 * inside a tooltip would silently fall back to the map-marker palette, which is
 * the REQ-UI-132 contrast trap. Keep tooltip content textual.
 */
export const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(function TooltipContent({ className, sideOffset = 6, ...props }, ref) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          "z-50 max-w-[20rem] rounded-md border border-border",
          "bg-bg-overlay px-2.5 py-1.5 text-body-sm text-text-primary shadow-lg",
          "data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0",
          className,
        )}
        {...props}
      />
    </TooltipPrimitive.Portal>
  );
});

/** Props for :func:`DescriptionTooltip`. */
export interface DescriptionTooltipProps {
  /**
   * Stable test-id root, supplied by the host row. The trigger renders as
   * ``${testId}-help-toggle`` and the content as ``${testId}-help``.
   *
   * The ids are deliberately **not** derived from ``label`` or ``description``:
   * both become deployment-configured strings under the Phase-E metric catalog,
   * and an id derived from display text silently retargets every selector when
   * the text changes — the exact failure REQ-STATUS-027(c) / REQ-STATUS-057
   * exist to prevent.
   */
  testId: string;
  /**
   * What the description is *about* — the row's label. Names the trigger for
   * assistive technology; the glyph alone announces nothing (REQ-UI-094).
   */
  label: string;
  /** The description. Rendered as text, never markup (see the module docstring). */
  description: string;
}

/**
 * The composed "ⓘ"-style affordance: a small focusable button that discloses
 * ``description`` in a tooltip (REQ-STATUS-056).
 *
 * Reachable by **all three** input paths:
 *   - **keyboard** — a real ``<button>`` is in the tab order, and Radix opens on
 *     focus, dismisses on ``Escape``, and sets ``aria-describedby`` while open;
 *   - **pointer** — hover after :const:`TOOLTIP_DELAY_MS`;
 *   - **touch** — click *toggles*. Radix's own click handler closes the tooltip,
 *     which leaves a touch operator (who never hovers and has no keyboard) with
 *     no way in at all; ``preventDefault`` on this handler suppresses Radix's
 *     close so the button behaves like the disclosure it looks like.
 *
 * Open state is controlled rather than left to Radix precisely so the click
 * toggle and the hover/focus/``Escape`` paths share one source of truth.
 *
 * A row with **no** description must render no trigger at all — a focusable
 * control with nothing to announce is worse than none (REQ-STATUS-056
 * Verification). That is the caller's job: render this component only when a
 * description exists.
 */
export function DescriptionTooltip({
  testId,
  label,
  description,
}: DescriptionTooltipProps): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger
        asChild
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          // Suppresses Radix's composed close-on-click (it honours
          // `defaultPrevented`), leaving this handler the only writer.
          event.preventDefault();
          setOpen((previous) => !previous);
        }}
      >
        <button
          type="button"
          aria-label={`What does ${label} mean?`}
          data-testid={`${testId}-help-toggle`}
          className="flex-none rounded-full border border-border px-1 text-meta leading-tight text-text-muted hover:bg-bg-elevated focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
        >
          ?
        </button>
      </TooltipTrigger>
      <TooltipContent data-testid={`${testId}-help`}>{description}</TooltipContent>
    </Tooltip>
  );
}
